import React, { useState, useEffect } from 'react';

const LandingView = ({ onStartTalking, onBreathe, recentConversation, onContinue, onOpenHistory, hasConversations, onMenuOpen, onOpenSurvey }) => {
  const [showGlow, setShowGlow] = useState(false);
  const [showPresence, setShowPresence] = useState(false);
  const [showGreeting1, setShowGreeting1] = useState(false);
  const [showGreeting2, setShowGreeting2] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [hideChecked, setHideChecked] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);

  const firstName = (localStorage.getItem('user_name') || '').split(' ')[0];

  // Determine welcome visibility
  const isHidden = localStorage.getItem('eunoia_hide_welcome') === 'true';
  const shouldShowWelcome = !isHidden;

  useEffect(() => {
    // Track visits — increment only once per session
    if (!sessionStorage.getItem('eunoia_counted')) {
      sessionStorage.setItem('eunoia_counted', 'true');
      const prev = parseInt(localStorage.getItem('eunoia_visit_count') || '0', 10);
      localStorage.setItem('eunoia_visit_count', String(prev + 1));
    }
    const count = parseInt(localStorage.getItem('eunoia_visit_count') || '1', 10);
    setIsFirstVisit(count <= 1);

    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Only animate on first landing this session
    const alreadyAnimated = sessionStorage.getItem('eunoia_landing_animated');

    if (alreadyAnimated) {
      setShowGlow(true);
      setShowPresence(true);
      setShowGreeting1(true);
      setShowGreeting2(true);
      setShowWelcome(true);
      setShowCards(true);
      return () => window.removeEventListener('resize', checkMobile);
    }

    // Entrance sequence timing
    const glowTimer = setTimeout(() => setShowGlow(true), 800);
    const presenceTimer = setTimeout(() => setShowPresence(true), 2000);
    const greeting1Timer = setTimeout(() => setShowGreeting1(true), 3500);
    const greeting2Timer = setTimeout(() => setShowGreeting2(true), 5000);
    const welcomeTimer = setTimeout(() => setShowWelcome(true), 5500);
    const cardsTimer = setTimeout(() => {
      setShowCards(true);
      sessionStorage.setItem('eunoia_landing_animated', 'true');
    }, shouldShowWelcome ? 7500 : 7000);

    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(glowTimer);
      clearTimeout(presenceTimer);
      clearTimeout(greeting1Timer);
      clearTimeout(greeting2Timer);
      clearTimeout(welcomeTimer);
      clearTimeout(cardsTimer);
    };
  }, []);

  const PillButton = ({ id, label, onClick, delay }) => (
    <button
      data-testid={`choice-${id}`}
      onClick={onClick}
      className="pill-btn"
      style={{
        width: '72%',
        minHeight: '58px',
        background: 'rgba(255, 243, 225, 0.05)',
        border: '1px solid rgba(255, 243, 225, 0.12)',
        borderRadius: '100px',
        padding: '0 28px',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'background 0.25s ease, border-color 0.25s ease, transform 0.25s ease, opacity 0.8s ease-out',
        transform: showCards ? 'scale(1)' : 'translateY(16px)',
        opacity: showCards ? 1 : 0,
        transitionDelay: showCards ? `${delay}s` : '0s',
        fontFamily: "'EB Garamond', Georgia, serif",
        fontSize: '19px',
        fontWeight: 400,
        letterSpacing: '0.04em',
        color: 'rgba(255, 243, 225, 0.88)',
        lineHeight: 1,
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      {label}
    </button>
  );

  return (
    <div 
      className="landing-entrance velvet-void" 
      data-testid="landing-view"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: '24px 0'
      }}
    >
      {/* Hamburger menu — upper left */}
      {onMenuOpen && (
        <button
          onClick={onMenuOpen}
          data-testid="landing-menu-btn"
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            zIndex: 50,
            WebkitTapHighlightColor: 'transparent',
            opacity: showGlow ? 1 : 0,
            transition: 'opacity 1s ease-out'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: '22px',
                height: '2px',
                borderRadius: '2px',
                background: 'rgba(255, 243, 225, 0.45)'
              }} />
            ))}
          </div>
        </button>
      )}

      {/* Center radial glow */}
      <div 
        className="center-glow"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at 50% 40%, rgba(62, 28, 18, 0.4) 0%, rgba(8, 6, 8, 0) 70%)',
          opacity: showGlow ? 1 : 0,
          transition: 'opacity 3s ease-out',
          pointerEvents: 'none'
        }}
      />

      {/* Main content */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        width: '100%',
        maxWidth: '400px',
        padding: '0 32px',
        position: 'relative',
        zIndex: 2
      }}>
        {/* Presence indicator - breathing velvet sphere */}
        <div 
          style={{
            opacity: showPresence ? 1 : 0,
            transition: 'opacity 1.5s ease-out',
            marginBottom: '32px'
          }}
        >
          <div className="breathing-sphere" />
        </div>

        {/* Greeting text */}
        <div style={{ 
          textAlign: 'center',
          marginBottom: '52px'
        }}>
          <p 
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: '24px',
              fontWeight: 400,
              color: 'rgba(255, 243, 225, 0.82)',
              opacity: showGreeting1 ? 1 : 0,
              transition: 'opacity 2s ease-out',
              margin: '0 0 16px 0'
            }}
          >
            Hi{firstName ? ` ${firstName}` : ''}…
          </p>
          <p 
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: '24px',
              fontWeight: 400,
              color: 'rgba(255, 243, 225, 0.82)',
              opacity: showGreeting2 ? 1 : 0,
              transition: 'opacity 2s ease-out',
              margin: 0
            }}
          >
            I'm here with you.
          </p>
        </div>

        {/* Welcome message */}
        {shouldShowWelcome && (
          <div style={{
            textAlign: 'center',
            maxWidth: '300px',
            margin: '0 auto',
            opacity: showWelcome ? 1 : 0,
            transform: showWelcome ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 1.2s ease-out, transform 1.2s ease-out',
            marginBottom: '20px'
          }}>
            <p style={{
              fontFamily: "'Cormorant', Georgia, serif",
              fontSize: '28px',
              fontWeight: 300,
              fontStyle: 'italic',
              color: 'rgba(255, 243, 225, 0.88)',
              margin: '0 0 24px 0'
            }}>
              Welcome to Eunoia
            </p>
            <p style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '17px',
              fontWeight: 400,
              lineHeight: 2.0,
              color: 'rgba(255, 243, 225, 0.65)',
              margin: 0,
              whiteSpace: 'pre-line'
            }}>
              {"You are not here to be fixed.\nYou are here to be heard.\n\nWhatever brought you to this moment \u2014 grief, confusion, longing, or simply the quiet need to be less alone \u2014 you are welcome here exactly as you are.\n\nThis is a space without judgment.\nWithout agenda.\nWithout rush.\n\nJust honest presence.\nWhenever you need it."}
            </p>

            {/* Checkbox — only from second visit onward */}
            {!isFirstVisit && (
              <label
                data-testid="hide-welcome-checkbox"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  marginTop: '20px',
                  marginBottom: '28px',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    const next = !hideChecked;
                    setHideChecked(next);
                    if (next) {
                      localStorage.setItem('eunoia_hide_welcome', 'true');
                    } else {
                      localStorage.removeItem('eunoia_hide_welcome');
                    }
                  }}
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '4px',
                    border: hideChecked
                      ? '1px solid rgba(196, 137, 74, 0.60)'
                      : '1px solid rgba(255, 243, 225, 0.25)',
                    background: hideChecked
                      ? 'rgba(196, 137, 74, 0.60)'
                      : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'all 0.2s ease',
                    flexShrink: 0
                  }}
                >
                  {hideChecked && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255, 243, 225, 0.90)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
                <span style={{
                  fontFamily: "'EB Garamond', Georgia, serif",
                  fontSize: '14px',
                  fontWeight: 400,
                  color: 'rgba(255, 243, 225, 0.35)',
                  marginLeft: '10px'
                }}>
                  don't show this again
                </span>
              </label>
            )}
          </div>
        )}

        {/* Three pill buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          width: '100%'
        }}>
          <PillButton
            id="new"
            label="New Conversation"
            onClick={onStartTalking}
            delay={0}
          />

          {hasConversations && (
            <PillButton
              id="continue"
              label="Past Conversations"
              onClick={onContinue}
              delay={0.12}
            />
          )}

          <PillButton
            id="breathe"
            label="Take a Breathe"
            onClick={onBreathe}
            delay={hasConversations ? 0.24 : 0.12}
          />
        </div>
      </div>

      {/* Mobile history icon - bottom right */}
      {isMobile && hasConversations && (
        <button
          onClick={onOpenHistory}
          data-testid="mobile-history-trigger"
          style={{
            position: 'absolute',
            bottom: '24px',
            right: '24px',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'rgba(196, 137, 74, 0.12)',
            border: '1px solid rgba(196, 137, 74, 0.25)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: showCards ? 1 : 0,
            transition: 'opacity 1s ease-out, background 0.2s ease',
            transitionDelay: '0.5s',
            zIndex: 50
          }}
          onTouchStart={(e) => e.currentTarget.style.background = 'rgba(196, 137, 74, 0.2)'}
          onTouchEnd={(e) => e.currentTarget.style.background = 'rgba(196, 137, 74, 0.12)'}
        >
          {/* Clock/History icon */}
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="rgba(196, 137, 74, 0.7)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </button>
      )}

      {/* CSS for breathing animation and button states */}
      <style>{`
        .presence-breathing {
          animation: presence-breath 4s ease-in-out infinite;
        }
        
        @keyframes presence-breath {
          0%, 100% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.4);
            opacity: 0.4;
          }
        }
        
        .pill-btn:active {
          background: rgba(196, 137, 74, 0.10) !important;
          border-color: rgba(196, 137, 74, 0.30) !important;
          transform: scale(0.98) !important;
        }
      `}</style>

      {/* Share your experience link */}
      {onOpenSurvey && (
        <button
          onClick={onOpenSurvey}
          data-testid="share-experience-link"
          style={{
            position: 'fixed',
            bottom: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '13px',
            color: 'rgba(255, 243, 225, 0.30)',
            padding: '8px 16px',
            zIndex: 10,
          }}
        >
          share your experience
        </button>
      )}
    </div>
  );
};

export default LandingView;
